import React, { useState, useMemo } from 'react';
import './SelectOppositionModal.scss';

function SelectOppositionModal({ teams, onClose, onSelectTeam, currentTeamAgeGroup, currentTeamLeague, teamType }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterSameLeague, setFilterSameLeague] = useState(false);
  const [filterSameAgeGroup, setFilterSameAgeGroup] = useState(false);

  // Deduplicate teams by team_id (in case API still returns duplicates)
  const uniqueTeams = useMemo(() => {
    const seen = new Map();
    return teams.filter(team => {
      if (!seen.has(team.team_id)) {
        seen.set(team.team_id, team);
        return true;
      }
      return false;
    });
  }, [teams]);

  const filteredTeams = useMemo(() => {
    let filtered = uniqueTeams;
    
    // Apply league filter - trim and compare case-insensitively
    if (filterSameLeague && currentTeamLeague) {
      const currentLeague = currentTeamLeague.trim();
      filtered = filtered.filter(team => {
        const teamLeague = (team.league_name || '').trim();
        return teamLeague && teamLeague.toLowerCase() === currentLeague.toLowerCase();
      });
    }
    
    // Apply age group filter
    if (filterSameAgeGroup && currentTeamAgeGroup) {
      filtered = filtered.filter(team => 
        team.age_group && team.age_group === currentTeamAgeGroup
      );
    }
    
    // Apply search query filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(team => {
        const teamName = (team.team_name || '').toLowerCase();
        const ageGroup = (team.age_group || '').toLowerCase();
        const leagueName = (team.league_name || '').toLowerCase();
        const managerName = (team.manager_name || '').toLowerCase();
        
        return teamName.includes(query) || 
               ageGroup.includes(query) || 
               leagueName.includes(query) ||
               managerName.includes(query);
      });
    }
    
    return filtered;
  }, [uniqueTeams, searchQuery, filterSameLeague, filterSameAgeGroup, currentTeamLeague, currentTeamAgeGroup]);

  const handleSelectTeam = (team) => {
    if (onSelectTeam) {
      onSelectTeam(team);
    }
    onClose();
  };

  return (
    <div className="select-opposition-modal modal-overlay" onClick={onClose}>
      <div className="modal-content modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>&times;</button>
        <h2>Change Your Opposition Team</h2>
        
        <div className="search-container">
          <input
            type="text"
            className="search-input"
            placeholder="Search by team name, age group, league, or manager..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            autoFocus
          />
          
          <div className="filter-checkboxes">
            <label className="filter-checkbox">
              <input
                type="checkbox"
                checked={filterSameLeague}
                onChange={(e) => setFilterSameLeague(e.target.checked)}
                disabled={!currentTeamLeague}
              />
              <span>Same League Only</span>
            </label>
            {teamType !== 'squad' && (
              <label className="filter-checkbox">
                <input
                  type="checkbox"
                  checked={filterSameAgeGroup}
                  onChange={(e) => setFilterSameAgeGroup(e.target.checked)}
                  disabled={!currentTeamAgeGroup}
                />
                <span>Same Age Group Only</span>
              </label>
            )}
          </div>
        </div>

        <div className="teams-list">
          {filteredTeams.length === 0 ? (
            <div className="no-results">
              {searchQuery || filterSameLeague || filterSameAgeGroup 
                ? 'No teams found matching your filters.' 
                : 'No teams available.'}
            </div>
          ) : (
            filteredTeams.map((team) => (
              <div
                key={team.team_id}
                className="team-item"
                onClick={() => handleSelectTeam(team)}
              >
                <div className="team-main-info">
                  <div className="team-name">
                    {team.team_name}
                    {team.age_group && (
                      <span className="age-group"> ({team.age_group})</span>
                    )}
                  </div>
                  {team.league_name && (
                    <div className="league-name">{team.league_name}</div>
                  )}
                </div>
                {team.manager_name && (
                  <div className="manager-info">
                    Manager: {team.manager_name}
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        <div className="modal-actions">
          <button 
            type="button" 
            className="cancel-button" 
            onClick={onClose}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export default SelectOppositionModal;

